if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this.zoomScale = 1;
      this.minZoom = 0.75;
      this.maxZoom = 3;
      this.zoomStep = 0.25;
      this.panX = 0;
      this.panY = 0;
      this.isPanning = false;
      this.currentMediaIndex = -1;

      this.boundKeydown = this.onKeydown.bind(this);
      this.boundPointerDown = this.onPointerDown.bind(this);
      this.boundPointerMove = this.onPointerMove.bind(this);
      this.boundPointerUp = this.onPointerUp.bind(this);
      this.boundContentPointerUpGuard = this.onContentPointerUpGuard.bind(this);
    }

    hide() {
      document.removeEventListener('keydown', this.boundKeydown);
      this.resetZoom();
      super.hide();
    }

    show(opener) {
      this.ensureViewerControls();
      super.show(opener);
      this.showActiveMedia();

      document.removeEventListener('keydown', this.boundKeydown);
      document.addEventListener('keydown', this.boundKeydown);
    }

    getAllMediaItems() {
      const content = this.querySelector('.product-media-modal__content');
      if (!content) return [];

      return Array.from(content.children).filter((element) => element.hasAttribute('data-media-id'));
    }

    getImageMediaItems() {
      return this.getAllMediaItems().filter((element) => element.tagName === 'IMG');
    }

    getActiveMedia() {
      return this.querySelector('.product-media-modal__content > [data-media-id].active');
    }

    getZoomTarget(media = this.getActiveMedia()) {
      if (!media) return null;
      if (media.tagName === 'IMG') return media;
      return media.querySelector('img');
    }

    showActiveMedia() {
      const mediaId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
      const allItems = this.getAllMediaItems();

      allItems.forEach((element) => element.classList.remove('active'));

      let activeMedia = allItems.find((element) => element.getAttribute('data-media-id') === mediaId);
      if (!activeMedia) activeMedia = allItems[0];
      if (!activeMedia) return;

      activeMedia.classList.add('active');
      this.currentMediaIndex = this.getImageMediaItems().indexOf(activeMedia);

      this.resetViewerScroll();
      this.resetZoom();
      this.syncControls();

      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

      if (
        activeMedia.nodeName === 'DEFERRED-MEDIA' &&
        activeMediaContent &&
        activeMediaContent.querySelector('.js-youtube')
      ) {
        activeMedia.loadContent();
      }
    }

    activateImage(index) {
      const images = this.getImageMediaItems();
      if (!images.length) return;

      const normalizedIndex = (index + images.length) % images.length;
      this.getAllMediaItems().forEach((element) => element.classList.remove('active'));

      const activeMedia = images[normalizedIndex];
      activeMedia.classList.add('active');
      this.currentMediaIndex = normalizedIndex;

      this.resetViewerScroll();
      this.resetZoom();
      this.syncControls();
    }

    navigate(direction) {
      const images = this.getImageMediaItems();
      if (images.length < 2) return;

      let index = images.indexOf(this.getActiveMedia());
      if (index < 0) index = this.currentMediaIndex >= 0 ? this.currentMediaIndex : 0;

      this.activateImage(index + direction);
    }

    setZoom(scale) {
      const clamped = Math.min(this.maxZoom, Math.max(this.minZoom, scale));
      this.zoomScale = Math.round(clamped * 100) / 100;

      if (this.zoomScale <= 1) {
        this.panX = 0;
        this.panY = 0;
      }

      this.applyZoom();
      this.syncControls();
    }

    resetZoom() {
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
      this.isPanning = false;

      this.getImageMediaItems().forEach((image) => {
        image.style.setProperty('--mehdi-media-zoom', '1');
        image.style.setProperty('--mehdi-pan-x', '0px');
        image.style.setProperty('--mehdi-pan-y', '0px');
        image.classList.remove('mehdi-media-zoom-target', 'is-panning');
        image.setAttribute('draggable', 'false');
      });

      this.classList.remove('mehdi-media-is-zoomed');
      this.syncControls();
    }

    applyZoom() {
      const target = this.getZoomTarget();
      if (!target) return;

      target.classList.add('mehdi-media-zoom-target');
      target.setAttribute('draggable', 'false');
      target.style.setProperty('--mehdi-media-zoom', String(this.zoomScale));
      target.style.setProperty('--mehdi-pan-x', `${this.panX}px`);
      target.style.setProperty('--mehdi-pan-y', `${this.panY}px`);

      this.classList.toggle('mehdi-media-is-zoomed', this.zoomScale > 1);
    }

    resetViewerScroll() {
      const dialog = this.querySelector('[role="document"]');
      const content = this.querySelector('.product-media-modal__content');

      [dialog, content].forEach((element) => {
        if (!element) return;
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });
    }

    protectControl(element) {
      if (!element || element.dataset.mehdiProtected === 'true') return;

      const stop = (event) => event.stopPropagation();
      element.addEventListener('pointerdown', stop);
      element.addEventListener('pointerup', stop);
      element.dataset.mehdiProtected = 'true';
    }

    ensureViewerControls() {
      this.injectViewerStyles();

      if (!this.querySelector('.mehdi-media-nav--prev')) {
        const prev = this.createControlButton(
          'mehdi-media-nav mehdi-media-nav--prev',
          'Previous image',
          this.arrowIcon('left')
        );
        this.protectControl(prev);
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(-1);
        });
        this.appendChild(prev);
      }

      if (!this.querySelector('.mehdi-media-nav--next')) {
        const next = this.createControlButton(
          'mehdi-media-nav mehdi-media-nav--next',
          'Next image',
          this.arrowIcon('right')
        );
        this.protectControl(next);
        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(1);
        });
        this.appendChild(next);
      }

      if (!this.querySelector('.mehdi-media-zoom-controls')) {
        const zoomControls = document.createElement('div');
        zoomControls.className = 'mehdi-media-zoom-controls';
        zoomControls.setAttribute('role', 'group');
        zoomControls.setAttribute('aria-label', 'Image zoom controls');
        this.protectControl(zoomControls);

        const zoomOut = this.createControlButton(
          'mehdi-media-zoom-button mehdi-media-zoom-out',
          'Zoom out',
          this.minusIcon()
        );
        const zoomReset = this.createControlButton(
          'mehdi-media-zoom-level',
          'Reset image to fit',
          '100%'
        );
        const zoomIn = this.createControlButton(
          'mehdi-media-zoom-button mehdi-media-zoom-in',
          'Zoom in',
          this.plusIcon()
        );

        [zoomOut, zoomReset, zoomIn].forEach((button) => this.protectControl(button));

        zoomOut.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setZoom(this.zoomScale - this.zoomStep);
        });

        zoomReset.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setZoom(1);
        });

        zoomIn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setZoom(this.zoomScale + this.zoomStep);
        });

        zoomControls.append(zoomOut, zoomReset, zoomIn);
        this.appendChild(zoomControls);
      }

      this.prevButton = this.querySelector('.mehdi-media-nav--prev');
      this.nextButton = this.querySelector('.mehdi-media-nav--next');
      this.zoomControls = this.querySelector('.mehdi-media-zoom-controls');
      this.zoomOutButton = this.querySelector('.mehdi-media-zoom-out');
      this.zoomInButton = this.querySelector('.mehdi-media-zoom-in');
      this.zoomLevelButton = this.querySelector('.mehdi-media-zoom-level');

      const content = this.querySelector('.product-media-modal__content');

      if (content && content.dataset.mehdiViewerEventsAttached !== 'true') {
        content.addEventListener('pointerdown', this.boundPointerDown);
        content.addEventListener('pointermove', this.boundPointerMove);
        content.addEventListener('pointerup', this.boundPointerUp);
        content.addEventListener('pointerup', this.boundContentPointerUpGuard);
        content.addEventListener('pointercancel', this.boundPointerUp);
        content.dataset.mehdiViewerEventsAttached = 'true';
      }

      this.syncControls();
    }

    syncControls() {
      const images = this.getImageMediaItems();
      const activeMedia = this.getActiveMedia();
      const currentIndex = images.indexOf(activeMedia);

      if (currentIndex >= 0) this.currentMediaIndex = currentIndex;

      const canNavigate = images.length > 1 && currentIndex >= 0;
      if (this.prevButton) this.prevButton.hidden = !canNavigate;
      if (this.nextButton) this.nextButton.hidden = !canNavigate;

      const canZoom = activeMedia && activeMedia.tagName === 'IMG';
      if (this.zoomControls) this.zoomControls.hidden = !canZoom;

      if (this.zoomOutButton) this.zoomOutButton.disabled = this.zoomScale <= this.minZoom;
      if (this.zoomInButton) this.zoomInButton.disabled = this.zoomScale >= this.maxZoom;
      if (this.zoomLevelButton) this.zoomLevelButton.textContent = `${Math.round(this.zoomScale * 100)}%`;
    }

    onContentPointerUpGuard(event) {
      const activeImage = this.getZoomTarget();
      if (activeImage && event.target === activeImage) {
        event.stopPropagation();
      }
    }

    onKeydown(event) {
      if (!this.hasAttribute('open')) return;

      const tagName = event.target && event.target.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(1);
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setZoom(this.zoomScale + this.zoomStep);
      } else if (event.key === '-') {
        event.preventDefault();
        this.setZoom(this.zoomScale - this.zoomStep);
      }
    }

    onPointerDown(event) {
      if (this.zoomScale <= 1 || event.button > 0) return;

      const target = this.getZoomTarget();
      if (!target || event.target !== target) return;

      event.preventDefault();
      event.stopPropagation();

      this.isPanning = true;
      this.activePointerId = event.pointerId;
      this.panStartX = event.clientX - this.panX;
      this.panStartY = event.clientY - this.panY;
      target.classList.add('is-panning');

      if (target.setPointerCapture) target.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isPanning || event.pointerId !== this.activePointerId) return;

      event.preventDefault();
      this.panX = event.clientX - this.panStartX;
      this.panY = event.clientY - this.panStartY;
      this.applyZoom();
    }

    onPointerUp(event) {
      if (!this.isPanning || event.pointerId !== this.activePointerId) return;

      event.preventDefault();
      event.stopPropagation();

      const target = this.getZoomTarget();
      if (target) {
        target.classList.remove('is-panning');

        if (
          target.releasePointerCapture &&
          target.hasPointerCapture &&
          target.hasPointerCapture(event.pointerId)
        ) {
          target.releasePointerCapture(event.pointerId);
        }
      }

      this.isPanning = false;
      this.activePointerId = null;
    }

    createControlButton(className, ariaLabel, content) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.setAttribute('aria-label', ariaLabel);
      button.innerHTML = content;
      return button;
    }

    arrowIcon(direction) {
      const points = direction === 'left' ? '15 5 8 12 15 19' : '9 5 16 12 9 19';
      return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="${points}"></polyline></svg>`;
    }

    minusIcon() {
      return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 12h12"></path></svg>';
    }

    plusIcon() {
      return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 6v12M6 12h12"></path></svg>';
    }

    injectViewerStyles() {
      if (document.getElementById('mehdi-product-viewer-controls-style')) return;

      const style = document.createElement('style');
      style.id = 'mehdi-product-viewer-controls-style';
      style.textContent = `
        .product-media-modal .mehdi-media-nav,
        .product-media-modal .mehdi-media-zoom-controls {
          position: fixed;
          z-index: 5;
        }

        .product-media-modal .mehdi-media-nav {
          top: 50%;
          width: 46px;
          height: 46px;
          padding: 0;
          border: 1px solid rgba(0, 0, 0, .14);
          border-radius: 50%;
          background: rgba(255, 255, 255, .94);
          color: #111;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-50%);
          cursor: pointer;
          box-shadow: 0 2px 14px rgba(0, 0, 0, .06);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        .product-media-modal .mehdi-media-nav:hover {
          border-color: rgba(0, 0, 0, .34);
          background: #fff;
        }

        .product-media-modal .mehdi-media-nav--prev { left: 22px; }
        .product-media-modal .mehdi-media-nav--next { right: 22px; }

        .product-media-modal .mehdi-media-nav svg {
          width: 22px;
          height: 22px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.4;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .product-media-modal .mehdi-media-zoom-controls {
          left: 50%;
          bottom: 18px;
          display: flex;
          align-items: center;
          min-height: 44px;
          overflow: hidden;
          border: 1px solid rgba(0, 0, 0, .14);
          border-radius: 999px;
          background: rgba(255, 255, 255, .94);
          box-shadow: 0 2px 14px rgba(0, 0, 0, .06);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          transform: translateX(-50%);
        }

        .product-media-modal .mehdi-media-zoom-controls button {
          height: 42px;
          border: 0;
          background: transparent;
          color: #111;
          cursor: pointer;
        }

        .product-media-modal .mehdi-media-zoom-button {
          width: 44px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-media-modal .mehdi-media-zoom-button svg {
          width: 18px;
          height: 18px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.4;
          stroke-linecap: round;
        }

        .product-media-modal .mehdi-media-zoom-level {
          min-width: 58px;
          padding: 0 8px;
          border-left: 1px solid rgba(0, 0, 0, .09) !important;
          border-right: 1px solid rgba(0, 0, 0, .09) !important;
          font-family: var(--font-body-family);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: .06em;
        }

        .product-media-modal .mehdi-media-zoom-controls button:hover:not(:disabled) {
          background: rgba(0, 0, 0, .035);
        }

        .product-media-modal .mehdi-media-zoom-controls button:disabled {
          opacity: .3;
          cursor: default;
        }

        .product-media-modal .mehdi-media-nav:focus-visible,
        .product-media-modal .mehdi-media-zoom-controls button:focus-visible {
          outline: 1px solid #111;
          outline-offset: 3px;
        }

        .product-media-modal__content > img[data-media-id].active.mehdi-media-zoom-target {
          transform: translate(var(--mehdi-pan-x, 0px), var(--mehdi-pan-y, 0px)) scale(var(--mehdi-media-zoom, 1));
          transform-origin: center center;
          transition: transform .18s ease;
          will-change: transform;
          user-select: none;
          -webkit-user-select: none;
        }

        .product-media-modal.mehdi-media-is-zoomed .product-media-modal__content {
          touch-action: none;
        }

        .product-media-modal.mehdi-media-is-zoomed .product-media-modal__content > img[data-media-id].active {
          cursor: grab;
        }

        .product-media-modal .mehdi-media-zoom-target.is-panning {
          cursor: grabbing !important;
          transition: none !important;
        }

        .product-media-modal__toggle { z-index: 6 !important; }
        .product-media-modal [hidden] { display: none !important; }

        @media screen and (max-width: 749px) {
          .product-media-modal .mehdi-media-nav {
            width: 42px;
            height: 42px;
          }

          .product-media-modal .mehdi-media-nav--prev { left: 8px; }
          .product-media-modal .mehdi-media-nav--next { right: 8px; }

          .product-media-modal .mehdi-media-zoom-controls {
            bottom: max(12px, env(safe-area-inset-bottom));
          }

          .product-media-modal .mehdi-media-zoom-button { width: 42px; }
          .product-media-modal .mehdi-media-zoom-level { min-width: 54px; }
        }
      `;

      document.head.appendChild(style);
    }
  });
}
